/**
 * ModulePaneSkeleton — lightweight, presentational loading placeholder shown as
 * the Suspense fallback for code-split modules (see LAZY_CHUNK_FACTORIES in
 * lib/prefetch-module.ts) while their chunk loads. Replaces the previous bare
 * "Loading…" text so a cold first click lands on a shape-matched shimmer
 * instead of a blank pane.
 *
 * Pure presentation: no state, no data, no hooks. Reuses the shared `.skeleton`
 * shimmer treatment (index.css) and slots into the existing `.module-content`
 * pane layout so swapping in the real module doesn't jump the layout.
 */

import './ModulePaneSkeleton.css';

interface ModulePaneSkeletonProps {
  /** 'list' = stacked rows (lists/inboxes); 'grid' = card tiles. */
  variant?: 'list' | 'grid';
  /** Number of placeholder rows/tiles to render. */
  rows?: number;
}

export function ModulePaneSkeleton({ variant = 'list', rows = 5 }: ModulePaneSkeletonProps) {
  return (
    <div className="module-content module-pane-skeleton" aria-hidden="true">
      {variant === 'grid' ? (
        <div className="module-pane-skeleton-grid">
          {Array.from({ length: rows }).map((_, i) => (
            <div key={i} className="module-pane-skeleton-tile">
              <div className="skeleton module-pane-skeleton-tile-media" />
              <div className="skeleton module-pane-skeleton-line short" />
              <div className="skeleton module-pane-skeleton-line" />
            </div>
          ))}
        </div>
      ) : (
        <div className="module-pane-skeleton-list">
          {Array.from({ length: rows }).map((_, i) => (
            <div key={i} className="module-pane-skeleton-row">
              <div className="skeleton module-pane-skeleton-avatar" />
              <div className="module-pane-skeleton-row-text">
                <div className="skeleton module-pane-skeleton-line short" />
                <div className="skeleton module-pane-skeleton-line" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ModulePaneSkeleton;
